import type { ActionType, Facing, SequenceToken } from "../types/action";
import type { DebugSnapshot } from "../types/debug";
import type { GridPoint, PlayerGridPosition } from "../types/grid";
import type { LevelBreadcrumbData, LevelData, LevelInitialVisibility } from "../types/level";
import type { SymbolDictionaryData } from "../types/symbol";
import { createLevelBounds, isPointWithinBounds } from "./Grid";
import { parseSequence } from "./SymbolParser";

export interface LevelHistoryState {
  readonly length: number;
  readonly last5: readonly ActionType[];
}

export interface LevelFogState {
  readonly visible: readonly GridPoint[];
  readonly explored: readonly GridPoint[];
  readonly permanentReveal: boolean;
  readonly debugReveal: boolean;
}

export interface LevelCollectibleState {
  readonly local: readonly string[];
  readonly permanent: readonly string[];
}

export interface LevelBreadcrumbState {
  readonly active: readonly LevelBreadcrumbData[];
  readonly local: readonly string[];
}

export interface LevelSequenceState {
  readonly raw: readonly SequenceToken[];
  readonly expanded: readonly ActionType[];
  readonly playerNotes: Readonly<Record<string, string | null>>;
}

export interface LevelCompletionState {
  readonly cleared: boolean;
  readonly perfected: boolean;
}

export interface LevelUndoState {
  readonly player: PlayerGridPosition;
  readonly history: LevelHistoryState;
  readonly fog: LevelFogState;
  readonly collectibles: LevelCollectibleState;
  readonly breadcrumbs: LevelBreadcrumbState;
  readonly completion: LevelCompletionState;
}

export interface LevelUndoFrame {
  readonly state: LevelUndoState;
  readonly reason: string;
}

export interface LevelState {
  readonly tick: number;
  readonly player: PlayerGridPosition;
  readonly history: LevelHistoryState;
  readonly fog: LevelFogState;
  readonly collectibles: LevelCollectibleState;
  readonly breadcrumbs: LevelBreadcrumbState;
  readonly symbolSequence: LevelSequenceState;
  readonly completion: LevelCompletionState;
  readonly undoStack: readonly LevelUndoFrame[];
  readonly lastDebugSnapshot: DebugSnapshot | null;
}

export interface LevelStateInitializationOptions {
  readonly playerNotes?: Readonly<Record<string, string | null>>;
  readonly permanentReveal?: boolean;
  readonly debugReveal?: boolean;
  readonly triggeredBreadcrumbIds?: readonly string[];
}

function isRepeatToken(token: SequenceToken): boolean {
  return token === "3" || token === "5";
}

function createPointKey(point: GridPoint): string {
  return `${point.x},${point.y}`;
}

function createAllGridPoints(level: LevelData): readonly GridPoint[] {
  const points: GridPoint[] = [];

  for (let y = 0; y < level.grid.height; y += 1) {
    for (let x = 0; x < level.grid.width; x += 1) {
      points.push({ x, y });
    }
  }

  return points;
}

function collectInitialVisibilityPoints(
  level: LevelData,
  visibility: LevelInitialVisibility,
): readonly GridPoint[] {
  const bounds = createLevelBounds(level.grid);
  const visited = new Set<string>();
  const points: GridPoint[] = [];

  const appendPoint = (point: GridPoint): void => {
    if (!isPointWithinBounds(point, bounds)) {
      return;
    }

    const key = createPointKey(point);
    if (visited.has(key)) {
      return;
    }

    visited.add(key);
    points.push(point);
  };

  if (visibility.mode === "radius") {
    for (let y = level.spawn.y - visibility.radius; y <= level.spawn.y + visibility.radius; y += 1) {
      for (let x = level.spawn.x - visibility.radius; x <= level.spawn.x + visibility.radius; x += 1) {
        appendPoint({ x, y });
      }
    }

    return points;
  }

  for (const rect of visibility.rects) {
    for (let y = rect.y; y < rect.y + rect.height; y += 1) {
      for (let x = rect.x; x < rect.x + rect.width; x += 1) {
        appendPoint({ x, y });
      }
    }
  }

  return points;
}

function createPlayerNotes(
  rawTokens: readonly SequenceToken[],
  notes: Readonly<Record<string, string | null>>,
): Readonly<Record<string, string | null>> {
  const sequenceTokens = new Set<string>();
  for (const token of rawTokens) {
    if (!isRepeatToken(token)) {
      sequenceTokens.add(token);
    }
  }

  const playerNotes: Record<string, string | null> = {};
  for (const token of sequenceTokens) {
    playerNotes[token] = notes[token] ?? null;
  }

  return playerNotes;
}

export function createEmptyLevelState(facing: Facing = "right"): LevelState {
  return {
    tick: 0,
    player: {
      x: 0,
      y: 0,
      facing,
    },
    history: {
      length: 0,
      last5: [],
    },
    fog: {
      visible: [],
      explored: [],
      permanentReveal: false,
      debugReveal: false,
    },
    collectibles: {
      local: [],
      permanent: [],
    },
    breadcrumbs: {
      active: [],
      local: [],
    },
    symbolSequence: {
      raw: [],
      expanded: [],
      playerNotes: {},
    },
    completion: {
      cleared: false,
      perfected: false,
    },
    undoStack: [],
    lastDebugSnapshot: null,
  };
}

export function createInitialLevelState(
  level: LevelData,
  dictionary: SymbolDictionaryData,
  options: LevelStateInitializationOptions = {},
): LevelState {
  const parsedSequence = parseSequence(dictionary, level.symbolSequence);
  if (!parsedSequence.valid) {
    throw new Error(
      `Invalid symbol sequence for level ${level.id}: ${parsedSequence.reason ?? "unknown reason"}`,
    );
  }

  const permanentReveal = options.permanentReveal ?? false;
  const debugReveal = options.debugReveal ?? false;
  const initialVisibility = collectInitialVisibilityPoints(level, level.initialVisibility);
  const allGridPoints = createAllGridPoints(level);
  const explored = permanentReveal ? [...allGridPoints] : [...initialVisibility];
  const visible = (permanentReveal || debugReveal) ? [...allGridPoints] : [...initialVisibility];

  const triggeredBreadcrumbSet = new Set(options.triggeredBreadcrumbIds ?? []);
  const activeBreadcrumbs = level.breadcrumbs.filter(
    (b) => !b.uniqueTrigger || !triggeredBreadcrumbSet.has(b.id)
  );
  const startupTriggeredBreadcrumbIds = activeBreadcrumbs
    .filter((breadcrumb) => breadcrumb.triggerOnLevelStart)
    .map((breadcrumb) => breadcrumb.id);

  return {
    tick: 0,
    player: {
      x: level.spawn.x,
      y: level.spawn.y,
      facing: level.spawn.facing,
    },
    history: {
      length: 0,
      last5: [],
    },
    fog: {
      visible,
      explored,
      permanentReveal,
      debugReveal,
    },
    collectibles: {
      local: [],
      permanent: [],
    },
    breadcrumbs: {
      active: activeBreadcrumbs,
      local: startupTriggeredBreadcrumbIds,
    },
    symbolSequence: {
      raw: [...parsedSequence.raw],
      expanded: [...parsedSequence.expanded],
      playerNotes: createPlayerNotes(level.symbolSequence, options.playerNotes ?? {}),
    },
    completion: {
      cleared: false,
      perfected: false,
    },
    undoStack: [],
    lastDebugSnapshot: null,
  };
}
